import React from 'react';
import { Loader2Icon, CheckCircle2Icon, XCircleIcon, FileTextIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '../ui/card';

interface CreateEllieArtifactToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

const CreateEllieArtifactToolCallComponent: React.FC<CreateEllieArtifactToolCallProps> = ({
  args,
  argsText,
  result,
  status,
}) => {
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = !isRunning && !isError && result !== undefined;

  // Parse artifact name from args
  let artifactName = '';
  try {
    const parsed = typeof args === 'object' && args ? args : JSON.parse(argsText);
    artifactName = parsed?.name || '';
  } catch {
    artifactName = '';
  }

  // Parse result
  let isSuccess = false;
  let errorMessage = '';
  if (isComplete && result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      isSuccess = parsed?.success === true;
      if (!isSuccess) {
        errorMessage = parsed?.error || 'Failed to create artifact';
      }
    } catch {
      isSuccess = false;
    }
  }

  const hasError = isError || (isComplete && !isSuccess);

  return (
    <Card>
      <div className={cn(
        'p-3',
        isRunning
          ? 'bg-primary/10 border-b border-primary/20'
          : 'bg-muted/50 border-b border-border'
      )}>
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            {isRunning ? (
              <div className="flex items-center gap-2">
                <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">
                  Creating artifact{artifactName ? `: ${artifactName}` : ''}...
                </p>
              </div>
            ) : hasError ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <FileTextIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    Create Artifact
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
                  <p className="text-sm m-0 text-destructive">
                    {errorMessage || 'Failed to create artifact'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <FileTextIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    Create Artifact
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                  <p className="text-sm m-0 text-foreground">
                    Saved "{artifactName || 'Untitled'}" — view it on the Artifacts page
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export const CreateEllieArtifactToolCall = CreateEllieArtifactToolCallComponent as React.ComponentType<any>;
